import {useEffect} from 'react';

/** How often a held lock is refreshed, against the server's 120s lock TTL (`report.lock.ttl-seconds`).
 * Three beats per TTL: a session survives two consecutive missed beats (a slow request, a brief
 * network blip) before the lock is allowed to lapse, without polling so often that a heartbeat every
 * render cycle would be indistinguishable from a busy loop. */
export const HEARTBEAT_INTERVAL_MS = 40_000;

export interface UseLockHeartbeatOptions {
	/** Whether a heartbeat should be running at all — false while the lock is not held (unlocked,
	 * blocked, or already lost), so a stopped heartbeat never fires again until a fresh claim starts
	 * a new one. */
	active: boolean;
	/** Called on every tick. Takes no arguments and returns nothing: the caller closes over whatever
	 * it needs (report id, token) so this hook never has to know the shape of a lock claim. */
	beat: () => void;
}

/** Runs `beat` on `HEARTBEAT_INTERVAL_MS`, only while `active`. Stopping and restarting is driven by
 * `active` flipping, not by unmounting the caller — the effect's cleanup clears the interval on every
 * dependency change, so toggling `active` off always leaves no pending timer behind. */
export function useLockHeartbeat({active, beat}: UseLockHeartbeatOptions): void {
	useEffect(() => {
		if (!active) {
			return;
		}
		const id = setInterval(beat, HEARTBEAT_INTERVAL_MS);
		return () => clearInterval(id);
	}, [active, beat]);
}
